// API client for backend communication
class ApiClient {
    constructor() {
        // 在生产环境中使用相对路径，在开发环境中使用localhost
        const isDevelopment = process.env.NODE_ENV === 'development';
        this.baseURL = process.env.REACT_APP_API_BASE_URL || 
            (isDevelopment ? 'http://localhost:5003/api' : '/api');
        this.token = localStorage.getItem('token');
        this.unauthorizedCount = 0;
    }

    // Set authentication token
    setToken(token) {
        this.token = token;
        if (token) {
            localStorage.setItem('token', token);
        } else {
            localStorage.removeItem('token');
        }
    }

    // Get authentication headers
    getHeaders() {
        const headers = {
            'Content-Type': 'application/json',
        };

        if (this.token) {
            headers.Authorization = `Bearer ${this.token}`;
        }

        return headers;
    }

    /**
     * Fresh Idempotency-Key for one high-risk admin write.
     *
     * High-risk managed writes may require this header and answer 400
     * IdempotencyKeyRequired without it. Generate one per user-initiated attempt and reuse it
     * across retries of that same attempt.
     *
     * crypto.randomUUID is unavailable on http:// origins in some browsers, so fall back to a
     * timestamp+random string. Uniqueness per (actor, tenant, action) is all the server needs.
     */
    newIdempotencyKey() {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
        return `idem-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    }

    // Generic API request method
    async request(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        const { responseType, idempotencyKey, ...fetchOptions } = options;
        const headers = this.getHeaders();
        if (idempotencyKey) {
            headers['Idempotency-Key'] = idempotencyKey;
        }
        const config = {
            headers,
            ...fetchOptions,
        };

        try {
            const response = await fetch(url, config);
            
            if (response.status === 401) {
                this.unauthorizedCount = (this.unauthorizedCount || 0) + 1;
                const payload = {
                    endpoint,
                    count: this.unauthorizedCount,
                    at: new Date().toISOString(),
                };
                try {
                    sessionStorage.setItem('frk:last401', JSON.stringify(payload));
                } catch (storageError) {
                    console.warn('Unable to persist 401 debug info', storageError);
                }
                if (this.unauthorizedCount >= 3) {
                    this.setToken(null);
                    localStorage.removeItem('user');
                }
                const unauthorizedError = new Error('Unauthorized');
                unauthorizedError.status = 401;
                unauthorizedError.endpoint = endpoint;
                unauthorizedError.unauthorizedCount = this.unauthorizedCount;
                throw unauthorizedError;
            }

            if (!response.ok) {
                let errorMessage = `Request failed with status ${response.status}`;
                let errorBody = null;
                try {
                    const errorData = await response.json();
                    errorBody = errorData;
                    errorMessage =
                        errorData.error ||
                        errorData.message ||
                        (errorData.errors ? Object.values(errorData.errors).flat().join("; ") : null) ||
                        errorMessage;
                } catch (e) {
                    // Keep the default status message when the response body is not JSON.
                }
                const requestError = new Error(errorMessage);
                requestError.status = response.status;
                // Machine-readable companions to the human message: callers branch on errorCode
                // (e.g. VersionConflict) and quote traceId to support. Message precedence above is
                // unchanged, so existing callers keep behaving exactly as before.
                if (errorBody && typeof errorBody === 'object') {
                    // The managed-tenant endpoints answer { error, message }; older ones use
                    // { errorCode }. Accept both so callers can branch on a single property
                    // (IdempotencyKeyRequired, ConcurrencyConflict, PlatformAdminRequired, ...).
                    requestError.errorCode = errorBody.errorCode || errorBody.error || errorBody.code;
                    requestError.traceId = errorBody.traceId;
                    requestError.details = errorBody.details;
                }
                throw requestError;
            }

            // Handle 204 No Content responses
            if (response.status === 204) {
                this.unauthorizedCount = 0;
                return null;
            }

            if (responseType === 'blob') {
                this.unauthorizedCount = 0;
                return response.blob();
            }

            // Handle empty responses
            const text = await response.text();
            if (!text) {
                this.unauthorizedCount = 0;
                return null;
            }

            const parsed = JSON.parse(text);
            this.unauthorizedCount = 0;
            return parsed;
        } catch (error) {
            console.error(`API request failed: ${endpoint}`, error);
            throw error;
        }
    }

    // GET request
    async get(endpoint, options = {}) {
        const { params, ...otherOptions } = options;
        let url = endpoint;

        // Drop empty entries before building the query string. URLSearchParams
        // stringifies undefined/null into the LITERAL "undefined"/"null", and the
        // backend then treats that as a real filter value: a customer list asked for
        // `search=undefined` matches no company/code/account/name and comes back empty,
        // which reads as "the data is gone" rather than "the filter is nonsense".
        // Call sites already write `search: value || undefined` meaning "omit this",
        // so honouring that here is what every one of them assumes.
        if (params) {
            const entries = Object.entries(params).filter(
                ([, value]) => value !== undefined && value !== null
            );
            if (entries.length > 0) {
                url = `${endpoint}?${new URLSearchParams(entries).toString()}`;
            }
        }

        return this.request(url, { method: 'GET', ...otherOptions });
    }

    // POST request. options.idempotencyKey adds the Idempotency-Key header.
    async post(endpoint, data, options = {}) {
        return this.request(endpoint, {
            method: 'POST',
            body: JSON.stringify(data),
            ...options,
        });
    }

    // PUT request. options.idempotencyKey adds the Idempotency-Key header.
    async put(endpoint, data, options = {}) {
        return this.request(endpoint, {
            method: 'PUT',
            body: JSON.stringify(data),
            ...options,
        });
    }

    // DELETE request
    async delete(endpoint) {
        return this.request(endpoint, { method: 'DELETE' });
    }

    /**
     * Appends the mandatory target tenant to a write endpoint.
     *
     * Every asset/catalogue write resolves its target tenant through
     * TargetTenantContextResolver.ResolveAsync, which REFUSES to guess: a request without an
     * explicit ?tenantId= is rejected with 400 TenantIdRequired, whoever is signed in
     * (TargetTenantContextResolver.cs:93-99). A writer that forgets the parameter is therefore
     * not "mostly fine" — it can never succeed. Building the query in one place is what stops
     * the next writer from shipping broken; see api.tenant_writes.test.js, which walks this
     * list and fails if any writer drops it.
     */
    withTenant(endpoint, tenantId) {
        const id = Number(tenantId);
        if (!Number.isInteger(id) || id <= 0) {
            // Fail here rather than send `tenantId=undefined`: the message names the caller's
            // bug instead of surfacing as a generic model-binding 400 from the server.
            const error = new Error('TenantIdRequired');
            error.errorCode = 'TenantIdRequired';
            error.status = 400;
            error.endpoint = endpoint;
            throw error;
        }

        const separator = endpoint.includes('?') ? '&' : '?';
        return `${endpoint}${separator}tenantId=${encodeURIComponent(id)}`;
    }

    /**
     * Multipart upload.
     *
     * The Content-Type header is deliberately omitted: the browser has to generate the
     * multipart boundary itself, and the default application/json header would make the
     * server read the body as JSON and reject it. Authorization is still sent.
     */
    async upload(endpoint, formData) {
        const headers = {};
        if (this.token) {
            headers.Authorization = `Bearer ${this.token}`;
        }
        return this.request(endpoint, { method: 'POST', body: formData, headers });
    }

    // Authentication methods
    async login(account, password) {
        const response = await this.post('/auth/login', { account, password });
        if (response.token) {
            this.setToken(response.token);
        }
        return response;
    }

    async getCurrentUser() {
        return this.get('/auth/me');
    }

    logout() {
        this.setToken(null);
        localStorage.removeItem('user');
    }

    // Field Items
    async getFieldItems(params = {}) {
        return this.get('/fielditem', { params });
    }

    async getFieldItem(id) {
        return this.get(`/fielditem/${id}`);
    }

    async createFieldItem(fieldData, tenantId) {
        return this.post(this.withTenant('/fielditem', tenantId), fieldData);
    }

    async updateFieldItem(id, fieldData, tenantId) {
        return this.put(this.withTenant(`/fielditem/${id}`, tenantId), fieldData);
    }

    async deleteFieldItem(id, tenantId) {
        return this.delete(this.withTenant(`/fielditem/${id}`, tenantId));
    }

    // Users
    /**
     * @param {{tenantId?: number|string, assignableFor?: string}} [options]
     *   tenantId narrows to one tenant — a platform administrator sees every tenant without it.
     *   assignableFor narrows to the accounts allowed in that task person slot, decided by the
     *   same server-side table the create path validates against.
     */
    async getUsers(options = {}) {
        const query = new URLSearchParams();
        if (options.tenantId !== undefined && options.tenantId !== null && options.tenantId !== '') {
            query.set('tenantId', String(options.tenantId));
        }
        if (options.assignableFor) {
            query.set('assignableFor', options.assignableFor);
        }
        const suffix = query.toString();
        return this.get(suffix ? `/user?${suffix}` : '/user');
    }

    async getUser(id) {
        return this.get(`/user/${id}`);
    }

    async createUser(userData) {
        return this.post('/user', userData);
    }

    async updateUser(id, userData) {
        return this.put(`/user/${id}`, userData);
    }

    async deleteUser(id, tenantId) {
        return this.delete(`/user/${id}?tenantId=${encodeURIComponent(tenantId)}`);
    }

    // Tenant-managed writes use explicit tenant-nested routes. These are intentionally separate
    // from the legacy helpers so callers cannot accidentally omit the target tenant.
    async createManagedPerson(tenantId, userData) {
        return this.post(`/admin/tenants/${tenantId}/people`, userData);
    }

    async updateManagedPerson(tenantId, id, userData) {
        return this.put(`/admin/tenants/${tenantId}/people/${id}`, userData);
    }

    async disableManagedPerson(tenantId, id) {
        return this.delete(`/admin/tenants/${tenantId}/people/${id}`);
    }

    // Customer accounts are managed exactly like people now: inside one tenant, with the tenant in
    // the URL. There is deliberately no helper that takes a tenant from a form field.
    async getManagedCustomers(tenantId, params = {}, options = {}) {
        return this.get(`/admin/tenants/${tenantId}/customers`, { params, ...options });
    }

    async createManagedCustomer(tenantId, data) {
        return this.post(`/admin/tenants/${tenantId}/customers`, data);
    }

    async updateManagedCustomer(tenantId, id, data) {
        return this.put(`/admin/tenants/${tenantId}/customers/${id}`, data);
    }

    async disableManagedCustomer(tenantId, id) {
        return this.post(`/admin/tenants/${tenantId}/customers/${id}/disable`, {});
    }

    async enableManagedCustomer(tenantId, id) {
        return this.post(`/admin/tenants/${tenantId}/customers/${id}/enable`, {});
    }

    async getAccountIndex(params = {}, options = {}) {
        return this.get("/admin/account-index", { params, ...options });
    }

    async getPlatformAdmins(options = {}) {
        return this.get("/admin/platform-admins", options);
    }

    async grantPlatformAdmin(userId, reason) {
        return this.post(`/admin/platform-admins/${userId}/grant`, { reason });
    }

    async revokePlatformAdmin(userId, reason) {
        return this.post(`/admin/platform-admins/${userId}/revoke`, { reason });
    }

    async disablePlatformAdmin(userId, reason) {
        return this.post(`/admin/platform-admins/${userId}/disable`, { reason });
    }

    async getManagedTenantSummary(tenantId, options = {}) {
        return this.get(`/admin/tenants/${tenantId}/management-summary`, options);
    }

    async getManagedPeople(tenantId, options = {}) {
        return this.get(`/admin/tenants/${tenantId}/people`, options);
    }

    async getManagedInspectionSessions(tenantId, params = {}, options = {}) {
        return this.get(`/admin/tenants/${tenantId}/inspection-sessions`, { params, ...options });
    }

    async getManagedTenantAudit(tenantId, params = {}, options = {}) {
        return this.get(`/admin/tenants/${tenantId}/audit`, { params, ...options });
    }

    async getManagedAssetOwners(tenantId, options = {}) {
        return this.get(`/admin/tenants/${tenantId}/asset-owners`, options);
    }

    async getManagedFields(tenantId, { includeArchived = false } = {}, options = {}) {
        return this.get(`/admin/tenants/${tenantId}/fields`, {
            params: { includeArchived },
            ...options,
        });
    }

    async getManagedTurbines(tenantId, fieldId, { includeArchived = false } = {}, options = {}) {
        return this.get(`/admin/tenants/${tenantId}/fields/${fieldId}/turbines`, {
            params: { includeArchived },
            ...options,
        });
    }

    // The blades below a turbine. Tenant-nested on purpose: this route checks the target tenant,
    // the field grant AND that the turbine belongs to that field, none of which /api/leaf does
    // for a platform administrator — that one goes all-tenants by design, so passing it a
    // turbineId picked from an already-narrowed list would be a client-side boundary, not a
    // server-side one.
    //
    // There is deliberately NO helper here for the tenant-nested turbine/leaf manufacturer and
    // model routes. Those still filter on FieldItemId == fieldId, which stopped meaning "this
    // field's catalogue" when manufacturers and models became globally shared (design
    // 20260813_catalogue_global_sharing_design.md, shipped 2026-08-14). Calling them would miss
    // both the rows whose origin field is another one and the rows with no origin field at all,
    // and would present the remainder as if it were the field's complete catalogue. The
    // workbench derives its 厂商/机型 level from the models its turbines actually use instead.
    async getManagedTurbineLeaves(tenantId, fieldId, turbineId, options = {}) {
        return this.get(
            `/admin/tenants/${tenantId}/fields/${fieldId}/turbines/${turbineId}/leaves`,
            options,
        );
    }

    async grantManagedAssetOwner(tenantId, assetOwnerId) {
        return this.post(`/admin/tenants/${tenantId}/asset-owners/${assetOwnerId}/grant`, {});
    }

    async getManagedAssetOwnerRevokeImpact(tenantId, assetOwnerId) {
        return this.get(`/admin/tenants/${tenantId}/asset-owners/${assetOwnerId}/revoke-impact`);
    }

    async revokeManagedAssetOwner(tenantId, assetOwnerId) {
        return this.post(`/admin/tenants/${tenantId}/asset-owners/${assetOwnerId}/revoke`, { confirm: true });
    }

    async createManagedField(tenantId, fieldData) {
        return this.post(`/admin/tenants/${tenantId}/fields`, fieldData);
    }

    async updateManagedField(tenantId, id, fieldData) {
        return this.put(`/admin/tenants/${tenantId}/fields/${id}`, fieldData);
    }

    async deleteManagedField(tenantId, id) {
        return this.delete(`/admin/tenants/${tenantId}/fields/${id}`);
    }

    async restoreManagedField(tenantId, id) {
        return this.post(`/admin/tenants/${tenantId}/fields/${id}/restore`, {});
    }

    async restoreManagedTurbine(tenantId, fieldId, id) {
        return this.post(`/admin/tenants/${tenantId}/fields/${fieldId}/turbines/${id}/restore`, {});
    }

    // E3 device identity and rental periods.
    async getDevices() {
        return this.get('/admin/devices');
    }

    async createDevice(data) {
        return this.post('/admin/devices', data);
    }

    async updateDevice(id, data) {
        return this.put(`/admin/devices/${id}`, data);
    }

    async createDeviceRental(deviceId, data) {
        return this.post(`/admin/devices/${deviceId}/rentals`, data);
    }

    async deleteDeviceRental(deviceId, rentalId) {
        return this.delete(`/admin/devices/${deviceId}/rentals/${rentalId}`);
    }

    // E4 usage reporting: TaskCompletion counts and rolling DeviceRental months only.
    async getUsageSummary(params) {
        return this.get('/admin/usage/summary', { params });
    }

    async getUsageCompletions(params) {
        return this.get('/admin/usage/completions', { params });
    }

    async getUsageRentals(params) {
        return this.get('/admin/usage/rentals', { params });
    }

    async exportUsageCsv(params) {
        return this.get('/admin/usage/export.csv', { params, responseType: 'blob' });
    }

    async getUpdateSigningKeys(params = {}, options = {}) {
        return this.get('/admin/update-signing-keys', { params, ...options });
    }

    async generateUpdateSigningKey(data, idempotencyKey = this.newIdempotencyKey()) {
        return this.post('/admin/update-signing-keys/generate', data, { idempotencyKey });
    }

    async activateUpdateSigningKey(kid, data, idempotencyKey = this.newIdempotencyKey()) {
        return this.post(
            `/admin/update-signing-keys/${encodeURIComponent(kid)}/activate`,
            data,
            { idempotencyKey },
        );
    }


    // Tenants
    async getTenants(params = {}) {
        return this.get('/tenants', { params });
    }

    async getTenant(id) {
        return this.get(`/tenants/${id}`);
    }

    async createTenant(data) {
        return this.post('/tenants', data);
    }

    async updateTenant(id, data) {
        return this.put(`/tenants/${id}`, data);
    }

    async deleteTenant(id) {
        return this.delete(`/tenants/${id}`);
    }

    async grantTenantAssetOwnerAccess(tenantId, data) {
        return this.post(`/tenants/${tenantId}/asset-owners`, data);
    }

    async revokeTenantAssetOwnerAccess(tenantId, assetOwnerId) {
        return this.delete(`/tenants/${tenantId}/asset-owners/${assetOwnerId}`);
    }

    // Asset Owners
    async getAssetOwners(params = {}) {
        return this.get('/asset-owners', { params });
    }

    async getAssetOwner(id) {
        return this.get(`/asset-owners/${id}`);
    }

    async createAssetOwner(data) {
        return this.post('/asset-owners', data);
    }

    async updateAssetOwner(id, data) {
        return this.put(`/asset-owners/${id}`, data);
    }

    async deleteAssetOwner(id) {
        return this.delete(`/asset-owners/${id}`);
    }

    // Inspection Sessions
    async createInspectionSession(sessionData) {
        return this.post('/inspectionsession/create-with-tasks', sessionData);
    }

    async getInspectionSessions(params = {}) {
        return this.get('/inspectionsession', { params });
    }

    async getInspectionSessionTasks(sessionId, params = {}) {
        return this.get(`/inspectionsession/${sessionId}/tasks`, { params });
    }

    // Tasks
    async getTasks(params = {}) {
        return this.get('/task', { params });
    }

    async getTask(id) {
        return this.get(`/task/${id}`);
    }

    async createTask(taskData) {
        return this.post('/task', taskData);
    }

    // Create session-based task for a single turbine
    async createSessionTurbineTask(sessionId, turbineId) {
        return this.post(`/task/session/${sessionId}/turbine/${turbineId}`);
    }

    async createAnnotationTask(taskId, userId) {
        // This should go through the proxy to C# backend on port 5001
        return this.post(`/labelu/create-annotation-task/${taskId}`, {
            taskName: `Annotation Task ${taskId}`,
            taskDescription: 'Wind turbine blade inspection annotation',
            assignedUserId: userId
        });
    }

    // Media symlinks for LabelU visibility
    async createMediaSymlinks(taskId) {
        return this.post(`/media/symlinks/${taskId}`, {});
    }

    async updateTask(id, taskData) {
        return this.put(`/task/${id}`, taskData);
    }

    async releaseTask(id) {
        return this.post(`/task/${id}/release`, {});
    }

    async deleteTask(id) {
        return this.delete(`/task/${id}`);
    }

    // Defection types
    async getDefDefections() {
        return this.get('/defdefection');
    }

    async getDefDefection(id) {
        return this.get(`/defdefection/${id}`);
    }

    async createDefDefection(data) {
        return this.post('/defdefection', data);
    }

    async updateDefDefection(id, data) {
        return this.put(`/defdefection/${id}`, data);
    }

    async deleteDefDefection(id) {
        return this.delete(`/defdefection/${id}`);
    }

    // Turbines
    async getTurbines(params = {}) {
        return this.get('/turbine', { params });
    }

    async getTurbine(id) {
        return this.get(`/turbine/${id}`);
    }

    async createTurbine(turbineData, tenantId) {
        return this.post(this.withTenant('/turbine', tenantId), turbineData);
    }

    async createTurbineRange(turbineData, tenantId) {
        return this.post(this.withTenant('/turbine/batch-range', tenantId), turbineData);
    }

    async updateTurbine(id, turbineData, tenantId) {
        return this.put(this.withTenant(`/turbine/${id}`, tenantId), turbineData);
    }

    async deleteTurbine(id, tenantId) {
        return this.delete(this.withTenant(`/turbine/${id}`, tenantId));
    }

    async createManagedTurbine(tenantId, fieldId, turbineData) {
        return this.post(`/admin/tenants/${tenantId}/fields/${fieldId}/turbines`, turbineData);
    }

    async createManagedTurbineRange(tenantId, fieldId, turbineData) {
        return this.post(
            `/admin/tenants/${tenantId}/fields/${fieldId}/turbines/batch-range`,
            turbineData,
        );
    }

    async updateManagedTurbine(tenantId, fieldId, id, turbineData) {
        return this.put(
            `/admin/tenants/${tenantId}/fields/${fieldId}/turbines/${id}`,
            turbineData,
        );
    }

    async deleteManagedTurbine(tenantId, fieldId, id) {
        return this.delete(`/admin/tenants/${tenantId}/fields/${fieldId}/turbines/${id}`);
    }

    // Model Turbines
    // Turbine models carry a FieldItemId: a model belongs to exactly one field, and the
    // create/update write path rejects any (model, field) pair that does not match with
    // TurbineModelFieldMismatch. Callers picking a model for a known field MUST pass
    // fieldId, otherwise the picker offers models that cannot legally be selected.
    async getModelTurbines(params = {}) {
        return this.get('/modelturbine', { params });
    }

    async getModelTurbine(id) {
        return this.get(`/modelturbine/${id}`);
    }

    async createModelTurbine(modelTurbineData, tenantId) {
        return this.post(this.withTenant('/modelturbine', tenantId), modelTurbineData);
    }

    async updateModelTurbine(id, modelTurbineData, tenantId) {
        return this.put(this.withTenant(`/modelturbine/${id}`, tenantId), modelTurbineData);
    }

    async deleteModelTurbine(id, tenantId) {
        return this.delete(this.withTenant(`/modelturbine/${id}`, tenantId));
    }

    // Manu Turbines (Turbine Manufacturers)
    async getManuTurbines() {
        return this.get('/manuturbine');
    }

    async getManuTurbine(id) {
        return this.get(`/manuturbine/${id}`);
    }

    async createManuTurbine(manuTurbineData, tenantId) {
        return this.post(this.withTenant('/manuturbine', tenantId), manuTurbineData);
    }

    async updateManuTurbine(id, manuTurbineData, tenantId) {
        return this.put(this.withTenant(`/manuturbine/${id}`, tenantId), manuTurbineData);
    }

    async deleteManuTurbine(id, tenantId) {
        return this.delete(this.withTenant(`/manuturbine/${id}`, tenantId));
    }

    // Model Leaves
    async getModelLeaves(params = {}) {
        return this.get('/modelleaf', { params });
    }

    async getModelLeaf(id) {
        return this.get(`/modelleaf/${id}`);
    }

    async createModelLeaf(modelLeafData, tenantId) {
        return this.post(this.withTenant('/modelleaf', tenantId), modelLeafData);
    }

    async updateModelLeaf(id, modelLeafData, tenantId) {
        return this.put(this.withTenant(`/modelleaf/${id}`, tenantId), modelLeafData);
    }

    async deleteModelLeaf(id, tenantId) {
        return this.delete(this.withTenant(`/modelleaf/${id}`, tenantId));
    }

    // Manu Leaves (Blade Manufacturers)
    async getManuLeaves() {
        return this.get('/manuleaf');
    }

    async getManuLeaf(id) {
        return this.get(`/manuleaf/${id}`);
    }

    async createManuLeaf(manuLeafData, tenantId) {
        return this.post(this.withTenant('/manuleaf', tenantId), manuLeafData);
    }

    async updateManuLeaf(id, manuLeafData, tenantId) {
        return this.put(this.withTenant(`/manuleaf/${id}`, tenantId), manuLeafData);
    }

    async deleteManuLeaf(id, tenantId) {
        return this.delete(this.withTenant(`/manuleaf/${id}`, tenantId));
    }

    // Leaves
    async getLeaves() {
        return this.get('/leaf');
    }

    async getLeaf(id) {
        return this.get(`/leaf/${id}`);
    }

    async createLeaf(leafData, tenantId) {
        return this.post(this.withTenant('/leaf', tenantId), leafData);
    }

    async batchCreateLeavesByTurbineModel(data, tenantId) {
        return this.post(this.withTenant('/leaf/batch-by-turbine-model', tenantId), data);
    }

    async updateLeaf(id, leafData, tenantId) {
        return this.put(this.withTenant(`/leaf/${id}`, tenantId), leafData);
    }

    async deleteLeaf(id, tenantId) {
        return this.delete(this.withTenant(`/leaf/${id}`, tenantId));
    }

    // Enhanced leaf queries for service layer
    async getLeavesByField(fieldId, search = null) {
        const params = new URLSearchParams();
        if (fieldId) params.append('fieldId', fieldId);
        if (search) params.append('search', search);
        const queryString = params.toString();
        return this.get(`/leaf${queryString ? '?' + queryString : ''}`);
    }

    async getLeavesByTurbine(turbineId) {
        return this.get(`/leaf?turbineId=${turbineId}`);
    }

    // Raw Data
    async getRawData(taskId) {
        return this.get(`/rawdata?taskId=${taskId}`);
    }

    async createRawData(rawDataData) {
        return this.post('/rawdata', rawDataData);
    }

    async updateRawData(id, rawDataData) {
        return this.put(`/rawdata/${id}`, rawDataData);
    }

    async deleteRawData(id) {
        return this.delete(`/rawdata/${id}`);
    }

    // Frame Data
    async getFrameData(rawDataId) {
        return this.get(`/framedata?rawDataId=${rawDataId}`);
    }

    // Defections
    async getDefections(frameDataId) {
        return this.get(`/defection?frameDataId=${frameDataId}`);
    }

    async getDefectionsList(params = {}) {
        return this.get('/defection', { params });
    }

    async getDefection(id) {
        return this.get(`/defection/${id}`);
    }

    async createDefection(defectionData) {
        return this.post('/defection', defectionData);
    }

    async updateDefection(id, defectionData) {
        return this.put(`/defection/${id}`, defectionData);
    }

    async deleteDefection(id) {
        return this.delete(`/defection/${id}`);
    }

    async getDefectionsByTask(taskId) {
        return this.get(`/defection/by-task/${taskId}`);
    }

    // Statistics
    async getDashboardStats() {
        return this.get('/statistics/dashboard');
    }

    async getDefectionsByModel() {
        return this.get('/statistics/defections/by-model');
    }

    async getDefectionsByType() {
        return this.get('/statistics/defections/by-type');
    }
}

// Export singleton instance
export const apiClient = new ApiClient();
export default apiClient;
